import React from 'react';

interface LoadingSpinnerProps {
  type?: 'default' | 'conversations' | 'sending' | 'marking';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  hideText?: boolean;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  type, 
  size = 'md', 
  className = '',
  hideText = false
}) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };

  const getAnimation = () => {
    switch (type) {
      case 'conversations':
        return (
          <div className="flex items-center space-x-2">
            <div className={`${sizeClasses[size]} animate-spin rounded-full border-2 border-primary border-t-transparent`} />
            <span className="text-sm text-muted-foreground animate-pulse">Loading conversations...</span>
          </div>
        );
      
      case 'sending':
        return (
          <div className="flex items-center space-x-2">
            <div className="flex space-x-1">
              <div className="w-1 h-1 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-1 h-1 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-1 h-1 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            {!hideText && <span className="text-sm text-muted-foreground">Sending...</span>}
          </div>
        );
      
      case 'marking':
        return (
          <div className="flex items-center space-x-2">
            <div className={`${sizeClasses[size]} relative`}>
              <div className="absolute inset-0 rounded-full border-2 border-primary/20" />
              <div className="absolute inset-0 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            </div>
            <span className="text-sm text-muted-foreground">Marking as unread...</span>
          </div>
        );
      
      default:
        return (
          <div className={`${sizeClasses[size]} animate-spin rounded-full border-2 border-primary-foreground border-t-transparent`} />
        );
    }
  };

  return (
    <div className={`flex items-center justify-center ${className}`}>
      {getAnimation()}
    </div>
  );
};

// Pulse animation for conversation loading
export const ConversationSkeleton: React.FC = () => (
  <div className="space-y-3">
    {[...Array(5)].map((_, i) => (
      <div key={i} className="flex items-center space-x-3 p-3 rounded-lg bg-card animate-pulse">
        <div className="w-10 h-10 bg-muted rounded-full" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-muted rounded w-3/4" />
          <div className="h-3 bg-muted rounded w-1/2" />
        </div>
      </div>
    ))}
  </div>
);

// Message sending animation
export const SendingMessage: React.FC = () => (
  <div className="flex items-center justify-end space-x-2 p-3 bg-primary/10 rounded-lg">
    <div className="flex space-x-1">
      <div className="w-1 h-1 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
      <div className="w-1 h-1 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
      <div className="w-1 h-1 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
    </div>
    <span className="text-xs text-muted-foreground">Sending...</span>
  </div>
);

// Mark as unread animation
export const MarkingUnread: React.FC = () => (
  <div className="flex items-center justify-center p-2 bg-muted/50 rounded-lg">
    <div className="flex items-center space-x-2">
      <div className="w-4 h-4 relative">
        <div className="absolute inset-0 rounded-full border-2 border-primary/20" />
        <div className="absolute inset-0 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
      <span className="text-xs text-muted-foreground">Marking as unread...</span>
    </div>
  </div>
); 